import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { AuthProvider as OidcProvider, useAuth as useOidc } from 'react-oidc-context';
import { useAppConfig } from '../config/AppConfig.js';
import { STUDIOS_ROUTE } from '../domain/routes.js';
import { safeRedirectTarget } from './redirectTarget.js';

/**
 * One auth surface over two providers, chosen at runtime.
 *
 * Everything outside this directory calls `useClientAuth()` and never learns which mode is
 * active — wiring OIDC for real should not touch a single component.
 */

export type ClientAuth = {
    isLoading: boolean;
    isAuthenticated: boolean;
    /** The Cognito ID token. The server forwards it to the environments. */
    token: string | null;
    error: string | null;
    /** `redirectTo`: the same-origin path (pathname + search + hash) to land back on once signed in — see `onSigninCallback` below. */
    signIn: (redirectTo?: string) => void;
    signOut: () => void;
    /** Whether there is a real session to end — false under the stub, where signOut opens nothing. */
    canSignOut: boolean;
};

const StubContext = createContext<ClientAuth | null>(null);

const stubAuth: ClientAuth = {
    isLoading: false,
    isAuthenticated: true,
    token: null,
    error: null,
    signIn: () => {},
    signOut: () => {},
    canSignOut: false,
};

const OidcBridge = ({ children }: { children: ReactNode }) => {
    const oidc = useOidc();

    const value = useMemo<ClientAuth>(
        () => ({
            isLoading: oidc.isLoading,
            isAuthenticated: oidc.isAuthenticated,
            // The ID token, NOT the access token: the server verifies `token_use === 'id'`
            // and an access token 401s every request.
            token: oidc.user?.id_token ?? null,
            error: oidc.error?.message ?? null,
            signIn: (redirectTo) => void oidc.signinRedirect(redirectTo ? { state: redirectTo } : undefined),
            signOut: () => void oidc.removeUser(),
            canSignOut: true,
        }),
        [oidc],
    );

    return <StubContext.Provider value={value}>{children}</StubContext.Provider>;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const config = useAppConfig();

    if (config.authMode === 'stub' || !config.cognito) {
        return <StubContext.Provider value={stubAuth}>{children}</StubContext.Provider>;
    }

    return (
        <OidcProvider
            authority={config.cognito.authority}
            client_id={config.cognito.clientId}
            redirect_uri={window.location.origin}
            response_type="code"
            scope="openid email profile"
            // A real navigation, not a URL patch: `BrowserRouter` already resolved this
            // render's route (always "/", since `redirect_uri` is the origin) before this
            // callback runs, so only a fresh load can land it elsewhere — the page `signIn`
            // was called from (carried as this request's own `state`, see `redirectTarget.ts`),
            // or Studios with neither. This also drops the redirect's own `?code=&state=`, so
            // a refresh cannot replay them.
            onSigninCallback={(user) => window.location.replace(safeRedirectTarget(user?.state) ?? STUDIOS_ROUTE)}
        >
            <OidcBridge>{children}</OidcBridge>
        </OidcProvider>
    );
};

export const useClientAuth = (): ClientAuth => {
    const auth = useContext(StubContext);
    if (!auth) throw new Error('useClientAuth used outside AuthProvider');
    return auth;
};
